import React from "react"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {Link, useParams} from "react-router-dom"
import {useAccessToken} from "../../services/auth"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {useListReturnPath} from "../../hooks/useListReturnPath"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {
	findIncomingRealTimePaymentByRelatedInstructionId,
	getIncomingRealTimePayment,
	IncomingRealTimePayment,
	IncomingRealTimePaymentMessageType,
	matchingCreditTransferId,
} from "../../resources/incomingRealTimePayment"
import {ok} from "neverthrow"
import {kebabCase, startCase} from "lodash"
import classNames from "classnames"
import TitleBar, {MetaItemProps} from "../../components/TitleBar/TitleBar"
import {CustomerCard} from "../../components/Customers/CustomerCard"
import numeral from "numeral"
import moment from "moment"
import {OrgName} from "../../components/OrgName/OrgName"
import Card from "../../containers/Card/Card"
import ReactJson from "react-json-view"
import IncomingRtp from "../../components/IncomingRtp/IncomingRtp"
import {IncomingRtpButtons} from "../../components/IncomingRtp/IncomingRtpButtons"
import {NamesAtUnitCard} from "../../components/NamesAtUnit/NamesAtUnit"
import {UnitUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {Customer} from "../../resources/customer"
import {Transactions} from "../../components/Transactions/Transactions"
import {TransactionsColumns} from "../../components/Transactions/Transactions.types"
import {IncomingRtpColumns} from "../../components/IncomingRtp/IncomingRtp.types"
import {KeyValueCardView} from "../../components/RenderKeyValueCard/KeyValueCardView"
import {AuditLogDrawer} from "../../components/AuditLogDrawer/AuditLogDrawer"
import {AuditLogEventsHeaderMetaItemProps} from "../../components/AuditLogEventsHeader/AuditLogEventsHeader"
import {AuditLogActionButton} from "../../components/AuditLogActionButton/AuditLogActionButton"
import {useAuditLog} from "../../components/AuditLogDrawer/useAuditLog"

function RtpDetailsCard({rtp}: {rtp: IncomingRealTimePayment}) {
	const accessToken = useAccessToken()
	const {attributes, relationships} = rtp
	const isReturnRequest = attributes.messageType === IncomingRealTimePaymentMessageType.RequestForReturnOfFunds
	const relatedInstructionId = attributes.relatedCreditTransferInstructionId
	const relatedCreditTransfers = useAsyncResult(
		() =>
			isReturnRequest && relatedInstructionId
				? findIncomingRealTimePaymentByRelatedInstructionId(accessToken, relatedInstructionId)
				: Promise.resolve(ok({data: [] as IncomingRealTimePayment[]})),
		[accessToken, isReturnRequest, relatedInstructionId]
	)
	const relatedCreditTransferId = relatedCreditTransfers.match<React.ReactNode>(
		() => "",
		(document) => {
			const creditTransferId = relatedInstructionId && matchingCreditTransferId(document.data, relatedInstructionId)
			return creditTransferId ? (
				<Link className="link" to={`/incoming-real-time-payments/${creditTransferId}`}>
					{creditTransferId}
				</Link>
			) : (
				"N/A"
			)
		},
		() => "N/A"
	)
	const amount = attributes.amount && numeral(attributes.amount / 100).format("$0,0.00")
	const createdAt = attributes.createdAt && moment(attributes.createdAt).format("L LT")
	const updatedAt = attributes.updatedAt && moment(attributes.updatedAt).format("L LT")
	const org = relationships.org?.data.id && <OrgName orgId={relationships.org.data.id.toString()} element="span" />

	const details = {
		...(amount ? {amount} : {}),
		...(createdAt ? {createdAt} : {}),
		...(updatedAt ? {updatedAt} : {}),
		...(org ? {org} : {}),
		status: startCase(attributes.status),
		messageId: attributes.messageId,
		instructionId: attributes.instructionId,
		...(relatedInstructionId ? {relatedInstructionId} : {}),
		...(isReturnRequest ? {relatedCreditTransferId} : {}),
		...(attributes.returnRequestReasonCode ? {returnRequestReasonCode: attributes.returnRequestReasonCode} : {}),
		...(attributes.bankRoutingNumber ? {bankRoutingNumber: attributes.bankRoutingNumber} : {}),
		...(attributes.settlementId ? {settlementId: attributes.settlementId} : {}),
		...(attributes.creditTransferRejectReason
			? {creditTransferRejectReasonCode: attributes.creditTransferRejectReason.code}
			: {}),
		...(attributes.returnRequestRejectReason
			? {returnRequestRejectReasonCode: attributes.returnRequestRejectReason}
			: {}),
		...(attributes.errorReason ? {errorReason: attributes.errorReason} : {}),
	}

	return <KeyValueCardView title={"RTP details"} details={details} />
}

function CounterpartyDetailsCard({rtp}: {rtp: IncomingRealTimePayment}) {
	const {counterpartyName, counterpartyRoutingNumber, counterpartyAccountNumber} = rtp.attributes

	const details = {
		...(counterpartyName ? {name: counterpartyName} : {}),
		...(counterpartyRoutingNumber ? {routingNumber: counterpartyRoutingNumber} : {}),
		...(counterpartyAccountNumber ? {accountNumber: counterpartyAccountNumber} : {}),
	}

	return <KeyValueCardView title={"Counterparty Details"} details={details} />
}

function FullDetails({rawData}: {rawData: object}) {
	return (
		<Card title="Raw data">
			<ReactJson src={rawData} displayDataTypes={false} />
		</Card>
	)
}

export function IncomingRealTimePaymentInternal({
	value: rtp,
	refresh,
}: {
	value: {data: IncomingRealTimePayment; included: Array<Customer>}
	refresh: () => void
}) {
	const listReturnPath = useListReturnPath("/incoming-rtp/")
	const {showAuditLog, openAuditLogDrawer, closeAuditLogDrawer, isAuditLogDrawerOpened} = useAuditLog()
	const auditLogEventsHeaderMetaItems: AuditLogEventsHeaderMetaItemProps[] = []
	const accessToken = useAccessToken()
	const {attributes, relationships, id} = rtp.data

	const customers =
		rtp.included &&
		(rtp.included.filter((i) => ["individualCustomer", "businessCustomer"].includes(i.type)) as Customer[])
	const singleCustomer = Array.isArray(customers) && customers.length === 1 ? customers[0] : null

	const hasCounterparty =
		!!attributes.counterpartyName || !!attributes.counterpartyRoutingNumber || !!attributes.counterpartyAccountNumber

	const accountId = relationships.account?.data.id
	const transactionId = relationships.transaction?.data.id
	const customerId = relationships.customer?.data.id

	return (
		<>
			<AuditLogDrawer
				open={isAuditLogDrawerOpened}
				onClose={closeAuditLogDrawer}
				auditLogEventsHeaderMetaItems={auditLogEventsHeaderMetaItems}
				resourceType="IncomingRealTimePayment"
				resourceId={rtp.data.id}
			/>

			<TitleBar
				title={`Incoming RTP #${id}`}
				breadcrumbs={[{text: "Incoming RTP", path: listReturnPath}]}
				status={{
					text: startCase(attributes.status),
					className: classNames("incoming-rtp-status", kebabCase(attributes.status)),
				}}
				meta={[
					...(accountId
						? [
								{
									text: "Account",
									icon: "bank-account--business-products",
									path: `/accounts/${accountId}`,
								} as MetaItemProps,
						  ]
						: []),
					...(customerId
						? [
								{
									text: "Customer",
									icon: "user-geometric-action-money-fund---users",
									path: `/customers/${customerId}`,
								} as MetaItemProps,
						  ]
						: []),
					...(singleCustomer?.relationships.application?.data.id
						? [
								{
									text: "Application",
									icon: "user-id-card",
									path: `/applications/${singleCustomer.relationships.application.data.id}`,
								} as MetaItemProps,
						  ]
						: []),
					...(accountId && transactionId
						? [
								{
									text: "Transaction",
									icon: "money-transfer--business-products",
									path: `/transaction/${accountId}/${transactionId}`,
								} as MetaItemProps,
						  ]
						: []),
				]}
			>
				{showAuditLog && <AuditLogActionButton onClick={openAuditLogDrawer} />}
				<IncomingRtpButtons incomingRtp={rtp.data} refresh={refresh} />
			</TitleBar>
			<div className={"incoming-rtp-page"}>
				<section className="section pr-3">
					<RtpDetailsCard rtp={rtp.data} />
					{customerId && (
						<>
							<CustomerCard customerId={customerId.toString()} />
							<UnitUserOnly>
								<NamesAtUnitCard customerId={customerId.toString()} />
							</UnitUserOnly>
						</>
					)}
					{hasCounterparty && <CounterpartyDetailsCard rtp={rtp.data} />}
					{accountId && (
						<Transactions
							enableAmountFilter={false}
							enableDateFilter={false}
							enableTypeFilter={false}
							enableOrgFilter={false}
							enableDirectionFilter={false}
							isUsingPaging={false}
							fullHeight={false}
							accountId={accountId.toString()}
							limit={5}
							enableSearch={false}
							enableExport={false}
							title={"Recent Transactions"}
							token={accessToken}
							includedColumns={[
								TransactionsColumns.id,
								TransactionsColumns.type,
								TransactionsColumns.summary,
								TransactionsColumns.createdAt,
								TransactionsColumns.balance,
								TransactionsColumns.amount,
							]}
						/>
					)}

					<IncomingRtp
						title={"Recent Incoming RTPs"}
						fullHeight={false}
						stickyAction={false}
						enablePaging={false}
						enableStatusFilter={false}
						enableOrgFilter={false}
						enableBankFilter={false}
						enableAmountFilter={false}
						limit={10}
						includedColumns={[
							IncomingRtpColumns.id,
							IncomingRtpColumns.amount,
							IncomingRtpColumns.status,
							IncomingRtpColumns.createdAt,
						]}
					/>
				</section>
				<section className="section pl-3">
					{attributes.incomingMessage && <FullDetails rawData={attributes.incomingMessage} />}
				</section>
			</div>
		</>
	)
}

export default function IncomingRealTimePaymentPage() {
	const {incomingRealTimePaymentId = ""} = useParams<{incomingRealTimePaymentId: string}>()
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const asyncResult = useAsyncResult<OkDocument<IncomingRealTimePayment>, ErrorDocument>(
		() => getIncomingRealTimePayment(accessToken, incomingRealTimePaymentId),
		[refreshToken, incomingRealTimePaymentId]
	)

	return (
		<AsyncResultComponent asyncResult={asyncResult} refresh={refresh}>
			{({value: value}) => {
				return (
					<IncomingRealTimePaymentInternal
						value={value as {data: IncomingRealTimePayment; included: Array<Customer>}}
						refresh={refresh}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}
